import React, { useState } from 'react';

/**
 * Normalizes API `deposit_instructions` (object or JSON string) to an `options` array.
 * Expected shape: `{ options: [ { type: "bank_account", ... }, { entity: "vpa", address: "..." } ] }`.
 */
export function parseDepositInstructionOptions(raw) {
  if (raw == null || raw === '') return [];
  let obj = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t === '') return [];
    try {
      obj = JSON.parse(t);
    } catch {
      return [];
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  const { options } = obj;
  if (!Array.isArray(options)) return [];
  return options.filter((o) => o && typeof o === 'object' && !Array.isArray(o));
}

function isVpaOption(opt) {
  if (!opt || typeof opt !== 'object') return false;
  if (opt.entity === 'vpa' || opt.type === 'vpa') return true;
  return typeof opt.address === 'string' && opt.address.trim() !== '' && opt.address.includes('@');
}

function isBankOption(opt) {
  if (!opt || typeof opt !== 'object') return false;
  if (isVpaOption(opt)) return false;
  if (opt.type === 'bank_account') return true;
  return typeof opt.account_number === 'string' && opt.account_number.trim() !== '';
}

function CopyValueButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const v = value == null ? '' : String(value).trim();
  if (!v) return null;

  const copy = async () => {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(v);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={label ? `Copy ${label}` : 'Copy'}
      className="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 shadow-sm hover:bg-slate-50"
    >
      <i className="material-icons-outlined text-sm">{copied ? 'check' : 'content_copy'}</i>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function formatNotes(notes) {
  if (notes == null) return null;
  if (Array.isArray(notes)) {
    const lines = notes.map((n) => String(n).trim()).filter(Boolean);
    return lines.length ? lines.join(' · ') : null;
  }
  if (typeof notes === 'string') {
    const t = notes.trim();
    return t === '' ? null : t;
  }
  return String(notes);
}

function BankOptionCard({ opt }) {
  const name = typeof opt.name === 'string' ? opt.name.trim() : '';
  const bankName = typeof opt.bank_name === 'string' ? opt.bank_name.trim() : '';
  const ifsc = typeof opt.ifsc === 'string' ? opt.ifsc.trim() : '';
  const accountNumber = typeof opt.account_number === 'string' ? opt.account_number.trim() : '';
  const notesText = formatNotes(opt.notes);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Bank transfer</p>
      {name ? (
        <div className="mt-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Account name</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">{name}</p>
        </div>
      ) : null}
      {bankName ? (
        <div className="mt-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Bank name</p>
          <p className="mt-0.5 text-sm text-slate-800">{bankName}</p>
        </div>
      ) : null}
      {accountNumber ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Account number</p>
            <p className="font-mono text-sm text-slate-900">{accountNumber}</p>
          </div>
          <CopyValueButton value={accountNumber} label="account number" />
        </div>
      ) : null}
      {ifsc ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">IFSC</p>
            <p className="font-mono text-sm text-slate-900">{ifsc}</p>
          </div>
          <CopyValueButton value={ifsc} label="IFSC" />
        </div>
      ) : null}
      {notesText ? <p className="mt-2 text-xs text-slate-600">{notesText}</p> : null}
    </div>
  );
}

function VpaOptionCard({ opt }) {
  const address = typeof opt.address === 'string' ? opt.address.trim() : '';
  if (!address) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">UPI / VPA</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <p className="min-w-0 break-all font-mono text-sm text-slate-900">{address}</p>
        <CopyValueButton value={address} label="UPI ID" />
      </div>
    </div>
  );
}

/**
 * Renders deposit funding options from wallet API `deposit_instructions`.
 * @param {{ depositInstructions: unknown, embedded?: boolean }} props — `embedded` strips the section chrome for use inside a modal.
 */
function WalletDepositInstructions({ depositInstructions, embedded = false }) {
  const options = parseDepositInstructionOptions(depositInstructions);
  if (options.length === 0) return null;

  const body = (
    <>
      {!embedded ? (
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Deposit options</p>
      ) : null}
      <p className="text-xs text-slate-600">
        Use these details when you add money to this wallet (bank transfer or UPI as applicable).
      </p>
      <ul className="mt-2 list-none space-y-3 p-0">
        {options.map((opt, i) => {
          if (isVpaOption(opt)) {
            return (
              <li key={`opt-${i}`}>
                <VpaOptionCard opt={opt} />
              </li>
            );
          }
          if (isBankOption(opt)) {
            return (
              <li key={`opt-${i}`}>
                <BankOptionCard opt={opt} />
              </li>
            );
          }
          return (
            <li key={`opt-${i}`}>
              <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2 font-mono text-xs text-slate-700">
                {JSON.stringify(opt)}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );

  if (embedded) {
    return <div className="space-y-2">{body}</div>;
  }

  return <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">{body}</div>;
}

export default WalletDepositInstructions;
